import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth, api } from '../context/AuthContext';
import { Eye, EyeOff, Sparkles, Plus, Trash2, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import AvatarPicker from './AvatarPicker';

const GRADES = [
  { value: 'K', label: 'Kindergarten' },
  { value: '1', label: '1st Grade' },
  { value: '2', label: '2nd Grade' },
];

const emptyChild = () => ({ name: '', grade: '', date_of_birth: '', avatar_url: null });

export function AuthModals({ isOpen, onClose, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1); // 1 = parent info, 2 = children, 3 = done
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [children, setChildren] = useState([emptyChild()]);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const resetForm = () => {
    setName(''); setEmail(''); setPassword('');
    setError(''); setStep(1);
    setChildren([emptyChild()]);
    setForgotSent(false);
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    resetForm();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        resetForm();
        onClose();
        const role = result.user?.role;
        navigate(role === 'admin' ? '/admin' : '/dashboard');
      } else {
        setError(result.error);
      }
    } finally { setIsLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: email.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setIsLoading(false); }
  };

  const handleParentStep = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || password.length < 6) {
      setError('Please fill all fields (password at least 6 characters).');
      return;
    }
    // Move to step 2 — we register only after children step so we can skip or add
    setStep(2);
  };

  const updateChild = (i, field, value) => {
    const next = [...children];
    next[i] = { ...next[i], [field]: value };
    setChildren(next);
  };

  const addChild = () => setChildren([...children, emptyChild()]);
  const removeChild = (i) => setChildren(children.filter((_, idx) => idx !== i));

  const handleRegisterSubmit = async (skipChildren = false) => {
    setError('');
    setIsLoading(true);
    try {
      // 1. Register parent (sets cookies so subsequent calls are authenticated)
      const regResult = await register(name, email, password, 'parent');
      if (!regResult.success) {
        setError(regResult.error);
        setStep(1);
        return;
      }

      // 2. Create children (if not skipped)
      if (!skipChildren) {
        const valid = children.filter((c) => c.name.trim() && c.grade);
        for (const c of valid) {
          try {
            await api.post('/api/children', {
              name: c.name.trim(),
              grade: c.grade,
              date_of_birth: c.date_of_birth || null,
              avatar_url: c.avatar_url || null,
              parent_id: regResult.user.id,
            });
          } catch {
            // continue even if one fails; we have account
          }
        }
      }

      // Navigate parent to dashboard
      resetForm();
      onClose();
      navigate('/dashboard');
    } finally { setIsLoading(false); }
  };

  const isLogin = mode === 'login';
  const isForgot = mode === 'forgot';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetForm(); onClose(); } }}>
      <DialogContent
        className="sm:max-w-[480px] rounded-3xl border border-slate-100 bg-white p-0 overflow-hidden"
        data-testid="auth-modal"
        aria-describedby="auth-modal-description"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] py-6 px-8 relative overflow-hidden">
          <motion.div
            className="absolute -top-4 -right-4 w-24 h-24 bg-[#FDE047]/30 rounded-full"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl md:text-3xl text-slate-800 flex items-center gap-3 tracking-tight">
              <Sparkles className="w-6 h-6 text-[#FD5E53]" />
              {isForgot ? 'Reset Password' : isLogin ? 'Welcome Back!' : step === 1 ? 'Join Craven-it!' : "Add Your Child"}
            </DialogTitle>
            <p id="auth-modal-description" className="text-slate-600 font-nunito text-sm mt-1">
              {isForgot
                ? 'Enter your email and we\'ll send you a reset link'
                : isLogin
                ? 'Sign in to check your child\'s progress'
                : step === 1
                  ? 'Create your parent account in seconds'
                  : 'Tell us about the little learner(s) you\'re signing up'}
            </p>
          </DialogHeader>
          {!isLogin && !isForgot && (
            <div className="flex items-center gap-2 mt-4">
              <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-[#FD5E53]' : 'bg-white/60'}`} />
              <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-[#FD5E53]' : 'bg-white/60'}`} />
            </div>
          )}
        </div>

        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border-b border-red-100 text-red-600 px-6 py-3 text-sm font-nunito"
              data-testid="auth-error"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* FORGOT PASSWORD */}
        {isForgot && (
          <form onSubmit={handleForgot} className="p-6 sm:p-8 space-y-5">
            {forgotSent ? (
              <div className="text-center space-y-3" data-testid="forgot-success">
                <CheckCircle2 className="w-12 h-12 text-[#16A34A] mx-auto" />
                <p className="font-nunito text-sm text-slate-600">
                  If an account exists for <span className="font-semibold text-slate-800">{email}</span>,
                  we've sent a reset link. Please check your inbox (and spam folder).
                </p>
                <button
                  type="button"
                  onClick={() => { setMode('login'); resetForm(); }}
                  className="font-nunito text-sm text-[#FD5E53] hover:underline"
                  data-testid="back-to-login-btn"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <>
                <FormField label="Email" id="email">
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com" required
                    className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 font-nunito"
                    data-testid="forgot-email-input" />
                </FormField>

                <PrimaryButton type="submit" loading={isLoading} data-testid="forgot-submit-btn">
                  Send reset link
                </PrimaryButton>

                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); resetForm(); }}
                    className="font-nunito text-sm text-slate-600 hover:text-[#FD5E53] transition-colors"
                    data-testid="forgot-back-link"
                  >
                    Remembered it? Back to sign in
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {/* LOGIN */}
        {isLogin && (
          <form onSubmit={handleLogin} className="p-6 sm:p-8 space-y-5">
            <FormField label="Email" id="email">
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com" required
                className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 font-nunito"
                data-testid="auth-email-input" />
            </FormField>

            <FormField label="Password" id="password">
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Your password" required
                  className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 pr-12 font-nunito"
                  data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </FormField>

            <PrimaryButton type="submit" loading={isLoading} data-testid="auth-submit-button">
              Sign In
            </PrimaryButton>

            <div className="text-center -mt-2">
              <button
                type="button"
                onClick={() => { setMode('forgot'); resetForm(); }}
                className="font-nunito text-sm text-slate-500 hover:text-[#FD5E53] transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>

            <SwitchModeLink onClick={switchMode} isLogin={isLogin} />
          </form>
        )}

        {/* REGISTER STEP 1 — Parent info */}
        {!isLogin && !isForgot && step === 1 && (
          <form onSubmit={handleParentStep} className="p-6 sm:p-8 space-y-5">
            <FormField label="Your Name" id="name">
              <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe" required
                className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 font-nunito"
                data-testid="register-name-input" />
            </FormField>

            <FormField label="Email" id="email">
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com" required
                className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 font-nunito"
                data-testid="auth-email-input" />
            </FormField>

            <FormField label="Password" id="password">
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" required minLength={6}
                  className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 pr-12 font-nunito"
                  data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </FormField>

            <PrimaryButton type="submit" loading={false} data-testid="auth-next-step-btn">
              Next: Add your child <ArrowRight className="w-4 h-4 inline ml-1.5" />
            </PrimaryButton>

            <SwitchModeLink onClick={switchMode} isLogin={isLogin} />
          </form>
        )}

        {/* REGISTER STEP 2 — Children */}
        {!isLogin && !isForgot && step === 2 && (
          <div className="p-6 sm:p-8 space-y-4 max-h-[70vh] overflow-y-auto">
            <AnimatePresence initial={false}>
              {children.map((child, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100"
                  data-testid={`register-child-${i}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-fredoka font-medium text-slate-700 text-sm">
                      Child #{i + 1}
                    </span>
                    {children.length > 1 && (
                      <button type="button" onClick={() => removeChild(i)}
                        className="p-1 rounded-full hover:bg-red-50 text-red-500"
                        data-testid={`remove-child-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Input
                      value={child.name}
                      onChange={(e) => updateChild(i, 'name', e.target.value)}
                      placeholder="Child's name (e.g., Emma)"
                      className="rounded-xl border-2 border-slate-100 focus:border-[#38BDF8] py-2.5 px-3 font-nunito text-sm bg-white"
                      data-testid={`child-name-${i}`}
                    />
                    <select
                      value={child.grade}
                      onChange={(e) => updateChild(i, 'grade', e.target.value)}
                      className="w-full rounded-xl border-2 border-slate-100 focus:border-[#38BDF8] py-2.5 px-3 font-nunito text-sm bg-white"
                      data-testid={`child-grade-${i}`}
                    >
                      <option value="">Select grade</option>
                      {GRADES.map((g) => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                    <div className="space-y-1">
                      <label
                        htmlFor={`dob-${i}`}
                        className="block text-xs font-nunito font-semibold text-slate-600"
                      >
                        Date of Birth <span className="text-slate-400 font-normal">(optional)</span>
                      </label>
                      <Input
                        id={`dob-${i}`}
                        type="date"
                        value={child.date_of_birth}
                        onChange={(e) => updateChild(i, 'date_of_birth', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="rounded-xl border-2 border-slate-100 focus:border-[#38BDF8] py-2.5 px-3 font-nunito text-sm bg-white"
                        data-testid={`child-dob-${i}`}
                      />
                    </div>
                    <AvatarPicker
                      value={child.avatar_url}
                      onChange={(url) => updateChild(i, 'avatar_url', url)}
                    />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            <button
              type="button"
              onClick={addChild}
              className="w-full py-2.5 rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#FD5E53] text-slate-500 hover:text-[#FD5E53] font-fredoka font-medium text-sm flex items-center justify-center gap-2 transition-colors"
              data-testid="add-another-child-btn"
            >
              <Plus className="w-4 h-4" /> Add another child
            </button>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={isLoading}
                className="px-4 py-3 rounded-full border-2 border-slate-100 text-slate-600 font-fredoka font-medium text-sm hover:bg-slate-50 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="back-step-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="button"
                onClick={() => handleRegisterSubmit(true)}
                disabled={isLoading}
                className="flex-1 px-4 py-3 rounded-full bg-white border-2 border-slate-100 text-slate-600 font-fredoka font-medium text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid="skip-children-btn"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={() => handleRegisterSubmit(false)}
                disabled={isLoading || !children.some((c) => c.name.trim() && c.grade)}
                className="flex-1 bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-sm py-3 rounded-full shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                data-testid="finish-register-btn"
              >
                {isLoading ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block"
                    />
                    Creating...
                  </>
                ) : (
                  <>Create account <CheckCircle2 className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, id, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="font-nunito font-semibold text-slate-700 text-sm">
        {label}
      </Label>
      {children}
    </div>
  );
}

function PrimaryButton({ children, loading, ...props }) {
  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      disabled={loading}
      className="w-full bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-base py-3.5 rounded-full shadow-md transition-all disabled:opacity-50"
      {...props}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <motion.span
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full inline-block"
          />
          Loading...
        </span>
      ) : children}
    </motion.button>
  );
}

function SwitchModeLink({ onClick, isLogin }) {
  return (
    <div className="text-center pt-1">
      <button
        type="button"
        onClick={onClick}
        className="font-nunito text-sm text-slate-600 hover:text-[#FD5E53] transition-colors"
        data-testid="auth-switch-mode"
      >
        {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
      </button>
    </div>
  );
}
